import { c as _c } from "react/compiler-runtime";
import { type JsonSerializable, useCopilotKit } from '@copilotkit/react-core/v2';
import { useLayoutEffect, useMemo } from 'react';

/**
 * Same availability values as {@link useCopilotFrontendTool}: boolean or
 * `'enabled'` / `'disabled'`. When disabled, context is not registered with CopilotKit
 * (stock `useAgentContext` has no native skip and would stringify `null` as `"null"`).
 */
export type CopilotAgentContextAvailability = boolean | 'enabled' | 'disabled';
export interface CopilotAgentContextArgs {
  /** A human-readable description of what this context represents */
  description: string;
  /** Context payload; ignored when `available` is disabled */
  value: JsonSerializable;
  /**
   * Whether to register this context with the agent. Defaults to enabled.
   * Mirrors frontend-tool `available`.
   */
  available?: CopilotAgentContextAvailability;
}
const isCopilotAgentContextAvailable = (available: CopilotAgentContextAvailability | undefined): boolean => {
  if (available === false || available === 'disabled') {
    return false;
  }
  return true;
};

/**
 * Alvie wrapper around CopilotKit `useAgentContext` with tool-style `available`
 * gating so callers can skip registration without conditional hooks.
 */
export const useCopilotAgentContext = t0 => {
  const $ = _c(7);
  const {
    description,
    value,
    available
  } = t0;
  const {
    copilotkit
  } = useCopilotKit();
  const isAvailable = isCopilotAgentContextAvailable(available);
  let t1;
  bb0: {
    if (!isAvailable) {
      t1 = null;
      break bb0;
    }
    if (typeof value === "string") {
      t1 = value;
      break bb0;
    }
    let t2;
    if ($[0] !== value) {
      t2 = JSON.stringify(value);
      $[0] = value;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    t1 = t2;
  }
  const stringValue = t1;
  let t2;
  let t3;
  if ($[2] !== copilotkit || $[3] !== description || $[4] !== stringValue) {
    t2 = () => {
      if (copilotkit == null || stringValue == null) {
        return;
      }
      const id = copilotkit.addContext({
        description,
        value: stringValue
      });
      return () => {
        copilotkit.removeContext(id);
      };
    };
    t3 = [description, stringValue, copilotkit];
    $[2] = copilotkit;
    $[3] = description;
    $[4] = stringValue;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useLayoutEffect(t2, t3);
};